import type { ReactNode } from 'react'
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

export interface SelectProps<T> {
  label: string
  options: T[]
  value?: T
  onSelect: (option: T) => void
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  labelStyle?: string
  optionStyle?: string
  buttonStyle?: string
  formatOptionForDisplay: (option: T) => string | ReactNode
}

export default function Select<T>({
  label,
  options,
  onSelect,
  value,
  size,
  labelStyle,
  optionStyle,
  buttonStyle,
  formatOptionForDisplay,
}: SelectProps<T>) {
  function handleSelectOption(option: T) {
    onSelect(option)
  }

  let sizePadding = ''
  let sizeText = ''
  let sizePr = ''
  let labelTextSize = ''
  let iconSize = ''

  switch (size) {
    case 'xs':
      sizePadding = 'px-2.5 py-1.5'
      sizeText = 'text-xs'
      sizePr = 'pr-8'
      labelTextSize = 'text-xs'
      iconSize = 'h-4 w-4'
      break
    case 'sm':
      sizePadding = 'px-3 py-2'
      sizeText = 'text-sm'
      sizePr = 'pr-9'
      labelTextSize = 'text-sm'
      iconSize = 'h-4 w-4'
      break
    case 'md':
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      sizePr = 'pr-10'
      labelTextSize = 'text-base'
      iconSize = 'h-5 w-5'
      break
    case 'lg':
      sizePadding = 'px-4 py-3'
      sizeText = 'text-lg'
      sizePr = 'pr-11'
      labelTextSize = 'text-lg'
      iconSize = 'h-6 w-6'
      break
    case 'xl':
      sizePadding = 'px-5 py-3.5'
      sizeText = 'text-xl'
      sizePr = 'pr-12'
      labelTextSize = 'text-xl'
      iconSize = 'h-6 w-6'
      break
    default:
      sizePadding = 'px-3.5 py-2.5'
      sizeText = 'text-base'
      sizePr = 'pr-10'
      labelTextSize = 'text-sm'
      iconSize = 'h-5 w-5'
  }

  const labelColor = value ? 'text-neutral-800' : 'text-neutral-400'
  const baseLabelStyles = `block font-semibold leading-6 ${labelColor} ${labelTextSize} font-sans`
  const defaultLabelStyle = labelStyle || baseLabelStyles

  const baseButtonStyles =
    'block w-full rounded-md text-neutral-800 shadow-sm transition-colors focus:outline-none bg-white font-sans font-extralight'
  const borderStyles = 'border border-slate-400 focus:ring-2 focus:ring-cta focus:border-transparent'
  const paddingStyles = `${sizePadding} ${sizePr}`
  const textStyles = `${sizeText} sm:leading-6`
  const defaultButtonStyles = `${baseButtonStyles} ${borderStyles} ${paddingStyles} ${textStyles} cursor-default text-left`
  const finalButtonStyles = `${defaultButtonStyles} ${buttonStyle || ''}`

  return (
    <div className='flex flex-col gap-2 w-full'>
      <Listbox value={value} onChange={handleSelectOption}>
        <Label className={defaultLabelStyle}>{label}</Label>
        <div className='relative w-full'>
          <ListboxButton className={finalButtonStyles}>
            <span className='block truncate min-h-5'>{value ? formatOptionForDisplay(value) : ''}</span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <ChevronUpDownIcon aria-hidden='true' className={`${iconSize} text-neutral-800`} />
            </span>
          </ListboxButton>

          <ListboxOptions
            transition
            className='absolute z-50 min-h-20 mt-1 max-h-60 w-full overflow-y-scroll rounded-md bg-white py-2 text-base shadow-lg border border-slate-400 ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm'
          >
            {options.map((option, index) => (
              <ListboxOption
                key={`select-option-${index}`}
                value={option}
                className={`group relative cursor-default select-none py-2 pl-3 pr-9 text-neutral-800 data-[focus]:bg-cta data-[focus]:text-white ${
                  optionStyle || ''
                }`}
              >
                <span
                  className={`block truncate font-extralight group-data-[selected]:font-semibold ${optionStyle || ''}`}
                >
                  {formatOptionForDisplay(option)}
                </span>

                <span className='absolute inset-y-0 right-0 flex items-center pr-2 text-cta group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden'>
                  <CheckIcon aria-hidden='true' className='h-5 w-5' />
                </span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  )
}
